import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import { CURRICULUM, GRADES, getAllChapters } from "../config/curriculum";
import { formatLatexForDisplay } from "../lib/latexUtils";

const ALL_CHAPTERS = getAllChapters();

const GOAL_LABELS = {
  ib_dp: "IB Board Exam (DP)",
  cambridge_igcse: "Cambridge (IGCSE / A-Levels)",
  cbse: "CBSE Board Exam",
  sat_math: "SAT Math Exam",
  jee_main_adv: "JEE Main / Advanced",
  general: "General Practice"
};

const FILTER_DROPDOWN_STYLE = {
  width: "165px",
  minWidth: "165px",
  padding: "8px 12px",
  borderRadius: "8px",
  border: "1px solid var(--border)",
  backgroundColor: "#fff",
  fontSize: "13px",
  boxSizing: "border-box",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  overflow: "hidden"
};

function AssignHomeworkModal({
  isOpen,
  onClose,
  data,
  rosterStudents,
  apiBase,
  token,
  onSuccess
}) {
  const [assignmentType, setAssignmentType] = useState("homework"); // "homework" | "test"
  const [targetGoal, setTargetGoal] = useState("ib_dp");
  const [classrooms, setClassrooms] = useState([]);
  const [selectedClassroomId, setSelectedClassroomId] = useState("");
  const [title, setTitle] = useState("");
  const [assignAll, setAssignAll] = useState(true);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [dueTimestamp, setDueTimestamp] = useState("");
  const [startTime, setStartTime] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [questionMarksMap, setQuestionMarksMap] = useState({});
  const [instructions, setInstructions] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalError, setModalError] = useState("");

  // Load tutor's classrooms
  useEffect(() => {
    if (!isOpen || !token) return;
    fetch(`${apiBase}/api/tutor/classrooms`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => (res.ok ? res.json() : { classrooms: [] }))
      .then((body) => setClassrooms(body.classrooms || []))
      .catch(() => setClassrooms([]));
  }, [isOpen, token, apiBase]);

  useEffect(() => {
    if (!data) return;
    const isTest = data.currentAssignmentType === "test" || data.sourceType === "exam_paper";
    setAssignmentType(isTest ? "test" : "homework");
    setTargetGoal(data.currentTargetGoal || "ib_dp");
    setSelectedClassroomId(data.currentClassroomId || "");

    const defaultTitle = isTest
      ? (data.title || "Mathematics Exam")
      : (data.title || "Homework Assignment");
    setTitle(defaultTitle);

    setAssignAll(data.currentAssignedAll ?? true);
    setSelectedStudents(Array.isArray(data.currentAssignedTo) ? data.currentAssignedTo : []);

    const initialDuration = data.currentDurationMinutes || 60;
    setDurationMinutes(initialDuration);

    if (data.currentStartTime) {
      const st = new Date(data.currentStartTime);
      setStartTime(new Date(st.getTime() - st.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
    } else {
      const now = new Date();
      now.setMinutes(Math.ceil(now.getMinutes() / 15) * 15, 0, 0);
      setStartTime(new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
    }

    if (data.currentDueTimestamp || data.currentDueDate) {
      const d = new Date(data.currentDueTimestamp || data.currentDueDate);
      setDueTimestamp(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
    } else if (isTest) {
      const now = new Date();
      now.setMinutes(Math.ceil(now.getMinutes() / 15) * 15, 0, 0);
      const end = new Date(now.getTime() + initialDuration * 60000);
      setDueTimestamp(new Date(end.getTime() - end.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
    } else {
      const def = new Date(Date.now() + 48 * 3600 * 1000);
      def.setHours(18, 0, 0, 0);
      setDueTimestamp(new Date(def.getTime() - def.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
    }

    setInstructions(data.currentInstructions || "");

    // Initialize marks map
    const marksInit = {};
    if (data.mode === "single" && data.question) {
      const qId = String(data.question.id || data.question._id);
      marksInit[qId] = 10;
    } else if (data.mode === "set" && data.set) {
      const qList = data.set.questionIds || data.set.questions || [];
      qList.forEach((q) => {
        const id = String(q._id || q.id || q);
        marksInit[id] = 10;
      });
    } else if (data.mode === "batch" && data.questionIds) {
      data.questionIds.forEach((id) => {
        marksInit[String(id)] = 10;
      });
    }
    setQuestionMarksMap(marksInit);
    setModalError("");
  }, [data]);

  if (!isOpen || !data) return null;

  // Derive candidate student roster based on chosen classroom
  const activeClassroom = classrooms.find((c) => String(c._id || c.id) === String(selectedClassroomId));
  const candidateStudents = activeClassroom
    ? rosterStudents.filter((st) => (activeClassroom.studentUids || []).includes(st.studentUid))
    : rosterStudents;

  // Calculate total marks across questions
  const totalTestMarks = Object.values(questionMarksMap).reduce((sum, m) => sum + (Number(m) || 0), 0);

  const handleStartTimeChange = (newStart) => {
    setStartTime(newStart);
    if (assignmentType === "test" && newStart && durationMinutes) {
      const d = new Date(newStart);
      const end = new Date(d.getTime() + Number(durationMinutes) * 60000);
      setDueTimestamp(new Date(end.getTime() - end.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
    }
  };

  const handleDurationChange = (newDur) => {
    setDurationMinutes(newDur);
    if (assignmentType === "test" && startTime && newDur) {
      const d = new Date(startTime);
      const end = new Date(d.getTime() + Number(newDur) * 60000);
      setDueTimestamp(new Date(end.getTime() - end.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setModalError("Please provide an assignment title.");
      return;
    }
    if (!assignAll && selectedStudents.length === 0) {
      setModalError("Please select at least one student or choose 'All students'.");
      return;
    }

    setIsSubmitting(true);
    setModalError("");

    try {
      const questionMarksArray = Object.entries(questionMarksMap).map(([qId, marks]) => ({
        questionId: qId,
        marks: Number(marks) || 10
      }));

      const payload = {
        title: title.trim(),
        assignmentType,
        targetGoal: assignmentType === "test" ? targetGoal : "general",
        classroomId: selectedClassroomId || null,
        assignedAll: assignAll,
        assignedTo: assignAll ? [] : selectedStudents,
        dueTimestamp: dueTimestamp ? new Date(dueTimestamp).toISOString() : null,
        dueDate: dueTimestamp ? new Date(dueTimestamp).toISOString() : null,
        startTime: assignmentType === "test" && startTime ? new Date(startTime).toISOString() : null,
        durationMinutes: assignmentType === "test" ? Number(durationMinutes) || 60 : null,
        questionMarks: questionMarksArray,
        instructions: instructions.trim()
      };

      if (data.mode === "single" && data.question) {
        payload.questionId = data.question.id || data.question._id;
      } else if (data.mode === "set" && data.set) {
        payload.setId = data.set.id || data.set._id;
      } else if (data.mode === "batch" && data.questionIds) {
        payload.questionIds = data.questionIds;
      }

      const res = await fetch(`${apiBase}/api/tutor/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const resData = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(resData.error || "Failed to dispatch assignment");
      }

      onSuccess(resData.message || "Assignment dispatched successfully!");
      onClose();
    } catch (err) {
      setModalError(err.message || "Failed to dispatch assignment.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(15, 23, 42, 0.65)",
        backdropFilter: "blur(4px)",
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px"
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "580px",
          maxHeight: "92vh",
          overflowY: "auto",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          border: "1px solid var(--border)"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#0f172a" }}>
              {assignmentType === "test" ? "⏱️ Dispatch Timed Test / Exam" : "📋 Assign Homework"}
            </h3>
            <div style={{ fontSize: "13px", color: "var(--muted)", marginTop: "2px" }}>
              {data.mode === "single"
                ? "Assign 1 question to classroom students"
                : data.mode === "batch"
                ? `Assign ${data.questionIds?.length || 0} selected problems as a set`
                : "Assign this question set to your classroom"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#64748b", lineHeight: 1 }}
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: "20px 24px" }}>
          {modalError && (
            <div
              style={{
                backgroundColor: "#fee2e2",
                color: "#991b1b",
                padding: "10px 14px",
                borderRadius: "8px",
                fontSize: "13px",
                marginBottom: "16px"
              }}
            >
              ⚠️ {modalError}
            </div>
          )}

          {/* ── Mode Toggle: Homework vs Timed Test ── */}
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: "18px",
              padding: "4px",
              backgroundColor: "#f1f5f9",
              borderRadius: "10px"
            }}
          >
            <button
              type="button"
              onClick={() => {
                setAssignmentType("homework");
                if (title === "Mathematics Exam") setTitle("Homework Problem Set");
              }}
              style={{
                flex: 1,
                padding: "9px 12px",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "13px",
                backgroundColor: assignmentType === "homework" ? "#ffffff" : "transparent",
                color: assignmentType === "homework" ? "#4f46e5" : "#64748b",
                boxShadow: assignmentType === "homework" ? "0 1px 3px rgba(0,0,0,0.1)" : "none"
              }}
            >
              📘 Homework / Assignment
            </button>
            <button
              type="button"
              onClick={() => {
                setAssignmentType("test");
                if (title === "Homework Problem Set" || title === "Homework Assignment") setTitle("Mathematics Exam");
              }}
              style={{
                flex: 1,
                padding: "9px 12px",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "13px",
                backgroundColor: assignmentType === "test" ? "#ffffff" : "transparent",
                color: assignmentType === "test" ? "#dc2626" : "#64748b",
                boxShadow: assignmentType === "test" ? "0 1px 3px rgba(0,0,0,0.1)" : "none"
              }}
            >
              ⏱️ Timed Test / Exam
            </button>
          </div>

          {/* Math preview for single question */}
          {data.mode === "single" && data.question?.latex && (
            <div
              style={{
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                padding: "12px 16px",
                marginBottom: "16px",
                maxHeight: "120px",
                overflowY: "auto"
              }}
            >
              <div style={{ fontSize: "11px", fontWeight: "700", color: "#64748b", marginBottom: "4px" }}>
                PROBLEM PREVIEW
              </div>
              <BlockMath math={formatLatexForDisplay(data.question.latex)} />
            </div>
          )}

          {/* Test Target Goal Dropdown (Test Mode Only) */}
          {assignmentType === "test" && (
            <div
              style={{
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "10px",
                padding: "14px",
                marginBottom: "18px"
              }}
            >
              <label style={{ display: "block", fontSize: "13px", fontWeight: "700", marginBottom: "6px", color: "#991b1b" }}>
                🎯 Exam / Curricular Target Goal *
              </label>
              <select
                value={targetGoal}
                onChange={(e) => setTargetGoal(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: "8px",
                  border: "1.5px solid #f87171",
                  fontSize: "13px",
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box"
                }}
              >
                <option value="ib_dp">Prepare for IB Board Exam (IB DP Mathematics)</option>
                <option value="cambridge_igcse">Prepare for Cambridge (IGCSE / A-Level)</option>
                <option value="cbse">Prepare for CBSE Board Exam</option>
                <option value="sat">Prepare for Digital SAT Math</option>
                <option value="jee">Prepare for JEE (Advanced / Mains)</option>
                <option value="general">General Mathematics Assessment</option>
              </select>
              <div style={{ fontSize: "11px", color: "#b91c1c", marginTop: "6px", lineHeight: "1.4" }}>
                The 100-point AI Rubric generator will calibrate scoring criteria according to this examination standard.
              </div>
            </div>
          )}

          {/* Assignment Title */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontSize: "13px", fontWeight: "700", marginBottom: "6px", color: "#0f172a" }}>
              {assignmentType === "test" ? "Test Title *" : "Assignment Title *"}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={assignmentType === "test" ? "e.g. Mid-Term Calculus Exam" : "e.g. Weekly Calculus Problem #4"}
              required
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: "8px",
                border: "1.5px solid #cbd5e1",
                fontSize: "14px",
                boxSizing: "border-box"
              }}
            />
          </div>

          {/* Per-Question Marks (Test Mode) */}
          {assignmentType === "test" && (
            <div
              style={{
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                padding: "14px",
                marginBottom: "18px"
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <span style={{ fontSize: "13px", fontWeight: "700", color: "#0f172a" }}>
                  📝 Marks Allocation
                </span>
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: "700",
                    color: "#4f46e5",
                    backgroundColor: "#ede9fe",
                    padding: "2px 8px",
                    borderRadius: "6px"
                  }}
                >
                  Total Test Marks: {totalTestMarks || 10}
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {Object.keys(questionMarksMap).map((qId, idx) => (
                  <div key={qId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                    <span style={{ fontSize: "13px", color: "#334155" }}>
                      Question {idx + 1} Marks:
                    </span>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={questionMarksMap[qId] ?? 10}
                      onChange={(e) => {
                        const val = Math.max(1, parseInt(e.target.value, 10) || 1);
                        setQuestionMarksMap((prev) => ({ ...prev, [qId]: val }));
                      }}
                      style={{
                        width: "80px",
                        padding: "6px 10px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        fontSize: "13px",
                        textAlign: "right"
                      }}
                    />
                  </div>
                ))}
              </div>
              <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "6px" }}>
                Question marks are displayed directly to students on the exam slate and used to calculate final percentages.
              </div>
            </div>
          )}

          {/* Classroom Selector */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontSize: "13px", fontWeight: "700", marginBottom: "6px", color: "#0f172a" }}>
              Select Classroom / Cohort
            </label>
            <select
              value={selectedClassroomId}
              onChange={(e) => {
                setSelectedClassroomId(e.target.value);
                setSelectedStudents([]);
              }}
              style={{
                width: "100%",
                padding: "9px 12px",
                borderRadius: "8px",
                border: "1.5px solid #cbd5e1",
                fontSize: "13px",
                backgroundColor: "#ffffff",
                boxSizing: "border-box"
              }}
            >
              <option value="">🌐 All Active Students (Global Roster - {rosterStudents.length} students)</option>
              {classrooms.map((cls) => (
                <option key={cls._id || cls.id} value={cls._id || cls.id}>
                  🏫 {cls.name} ({cls.studentCount || cls.studentUids?.length || 0} students)
                </option>
              ))}
            </select>
          </div>

          {/* Target Student Selection */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontSize: "13px", fontWeight: "700", marginBottom: "8px", color: "#0f172a" }}>
              Target Students in {activeClassroom ? activeClassroom.name : "Roster"} *
            </label>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="assignTarget"
                  checked={assignAll}
                  onChange={() => setAssignAll(true)}
                />
                <span>
                  <strong>All Students in {activeClassroom ? activeClassroom.name : "Classroom"}</strong> ({candidateStudents.length} students)
                </span>
              </label>

              <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="assignTarget"
                  checked={!assignAll}
                  onChange={() => setAssignAll(false)}
                />
                <span>Select Specific Students</span>
              </label>
            </div>

            {!assignAll && (
              <div
                style={{
                  marginTop: "10px",
                  maxHeight: "160px",
                  overflowY: "auto",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  backgroundColor: "#f8fafc"
                }}
              >
                {candidateStudents.length === 0 ? (
                  <div style={{ fontSize: "12px", color: "#64748b", padding: "8px 0" }}>
                    No students found in this group. Enroll students via Classrooms & Roster.
                  </div>
                ) : (
                  candidateStudents.map((st) => {
                    const checked = selectedStudents.includes(st.studentUid);
                    return (
                      <label
                        key={st.studentUid}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "13px",
                          padding: "5px 0",
                          cursor: "pointer"
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedStudents([...selectedStudents, st.studentUid]);
                            } else {
                              setSelectedStudents(selectedStudents.filter((id) => id !== st.studentUid));
                            }
                          }}
                        />
                        <span>
                          {st.name || st.username} {st.grade ? `(${st.grade})` : ""}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Exam Timing (Start Time, Duration, Deadline) */}
          {assignmentType === "test" ? (
            <div style={{ marginBottom: "18px", backgroundColor: "#fffbeb", border: "1.5px solid #fde68a", borderRadius: "10px", padding: "14px 16px" }}>
              <div style={{ fontSize: "13px", fontWeight: "700", color: "#92400e", marginBottom: "12px", display: "flex", alignItems: "center", gap: "6px" }}>
                <span>⏱️</span>
                <span>Scheduled Exam Window & Duration</span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "700", marginBottom: "4px", color: "#0f172a" }}>
                    Exam Start Time *
                  </label>
                  <input
                    type="datetime-local"
                    value={startTime}
                    onChange={(e) => handleStartTimeChange(e.target.value)}
                    required
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      border: "1.5px solid #cbd5e1",
                      fontSize: "13px",
                      backgroundColor: "#ffffff",
                      boxSizing: "border-box"
                    }}
                  />
                  <div style={{ fontSize: "11px", color: "#64748b", marginTop: "3px" }}>
                    Students cannot begin before this time.
                  </div>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "700", marginBottom: "4px", color: "#0f172a" }}>
                    Duration (Minutes) *
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="360"
                    step="5"
                    value={durationMinutes}
                    onChange={(e) => handleDurationChange(Math.max(5, parseInt(e.target.value, 10) || 60))}
                    required
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      border: "1.5px solid #cbd5e1",
                      fontSize: "13px",
                      backgroundColor: "#ffffff",
                      boxSizing: "border-box"
                    }}
                  />
                  {/* Duration Presets */}
                  <div style={{ display: "flex", gap: "4px", marginTop: "4px" }}>
                    {[30, 45, 60, 90, 120].map((mins) => (
                      <button
                        key={mins}
                        type="button"
                        onClick={() => handleDurationChange(mins)}
                        style={{
                          fontSize: "10px",
                          fontWeight: Number(durationMinutes) === mins ? "700" : "500",
                          backgroundColor: Number(durationMinutes) === mins ? "#d97706" : "#ffffff",
                          color: Number(durationMinutes) === mins ? "#ffffff" : "#475569",
                          border: "1px solid #cbd5e1",
                          borderRadius: "4px",
                          padding: "2px 6px",
                          cursor: "pointer"
                        }}
                      >
                        {mins}m
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "700", marginBottom: "4px", color: "#0f172a" }}>
                  Exam End Time / Sacrosanct Deadline *
                </label>
                <input
                  type="datetime-local"
                  value={dueTimestamp}
                  onChange={(e) => setDueTimestamp(e.target.value)}
                  required
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1.5px solid #ef4444",
                    fontSize: "13px",
                    backgroundColor: "#ffffff",
                    boxSizing: "border-box"
                  }}
                />
                <div style={{ fontSize: "11px", color: "#dc2626", marginTop: "4px", fontWeight: "500" }}>
                  🔒 Slate will lock and auto-submit all handwritten work when this deadline is reached.
                </div>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "700", marginBottom: "6px", color: "#0f172a" }}>
                Due Date & Time (Optional)
              </label>
              <input
                type="datetime-local"
                value={dueTimestamp}
                onChange={(e) => setDueTimestamp(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 14px",
                  borderRadius: "8px",
                  border: "1.5px solid #cbd5e1",
                  fontSize: "14px",
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box"
                }}
              />
            </div>
          )}

          {/* Instructions */}
          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontSize: "13px", fontWeight: "700", marginBottom: "6px", color: "#0f172a" }}>
              Teacher Instructions (Optional)
            </label>
            <textarea
              rows={2}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder={assignmentType === "test" ? "e.g. Closed-book exam. Show all intermediate workings on slate." : "e.g. Solve step-by-step using digital stylus."}
              style={{
                width: "100%",
                padding: "9px 14px",
                borderRadius: "8px",
                border: "1.5px solid #cbd5e1",
                fontSize: "13px",
                backgroundColor: "#f8fafc",
                boxSizing: "border-box",
                resize: "vertical"
              }}
            />
          </div>

          {/* Actions */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <button
              type="button"
              className="secondary-button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{ padding: "8px 18px", fontSize: "13px" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="primary-button"
              disabled={isSubmitting}
              style={{
                padding: "8px 22px",
                fontSize: "13px",
                fontWeight: "700",
                backgroundColor: assignmentType === "test" ? "#dc2626" : "#4f46e5"
              }}
            >
              {isSubmitting
                ? "Dispatching..."
                : assignmentType === "test"
                ? "🚀 Dispatch Timed Test"
                : "🚀 Dispatch Assignment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function MyQuestions({ apiBase, token, onPageBusyChange }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab State: "questions" | "groups"
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    ["questions", "groups"].includes(tabFromUrl) ? tabFromUrl : "questions"
  );

  useEffect(() => {
    const currentTabInUrl = searchParams.get("tab");
    if (currentTabInUrl === "homework") {
      navigate("/my-tutor", { replace: true });
      return;
    }
    if (currentTabInUrl && ["questions", "groups"].includes(currentTabInUrl) && currentTabInUrl !== activeTab) {
      setActiveTab(currentTabInUrl);
    }
  }, [searchParams, activeTab, navigate]);

  const handleTabChange = useCallback((nextTab) => {
    setActiveTab(nextTab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", nextTab);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Questions and Sets Data
  const [questions, setQuestions] = useState([]);
  const [questionSets, setQuestionSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Tutor Assignment & Selection State
  const [isTutor, setIsTutor] = useState(false);
  const [rosterStudents, setRosterStudents] = useState([]);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState(() => new Set());
  const [isGroupSelectMode, setIsGroupSelectMode] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState(() => new Set());
  const [assignModalData, setAssignModalData] = useState(null);
  const [assignSuccessToast, setAssignSuccessToast] = useState("");

  const toggleSelectQuestion = (qId) => {
    setSelectedQuestionIds((prev) => {
      const next = new Set(prev);
      if (next.has(qId)) next.delete(qId);
      else next.add(qId);
      return next;
    });
  };

  const toggleSelectGroup = (gId) => {
    setSelectedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(gId)) next.delete(gId);
      else next.add(gId);
      return next;
    });
  };

  const openAssignModalForSingle = (question) => {
    setAssignModalData({
      mode: "single",
      question,
      title: question.title || "Custom Homework Problem",
      currentAssignedAll: true,
      currentAssignedTo: [],
      currentDueDate: "",
      currentInstructions: ""
    });
  };

  const openAssignModalForSet = (set) => {
    setAssignModalData({
      mode: "set",
      set,
      title: set.title || "Homework Assignment",
      currentAssignedAll: set.assignedAll ?? true,
      currentAssignedTo: set.assignedTo || [],
      currentDueDate: set.dueDate || "",
      currentInstructions: set.instructions || ""
    });
  };

  const openAssignModalForBatch = () => {
    if (selectedQuestionIds.size === 0) return;
    setAssignModalData({
      mode: "batch",
      questionIds: Array.from(selectedQuestionIds),
      title: `Homework Assignment (${selectedQuestionIds.size} Problems)`,
      currentAssignedAll: true,
      currentAssignedTo: [],
      currentDueDate: "",
      currentInstructions: ""
    });
  };

  useEffect(() => {
    if (!assignSuccessToast) return;
    const timer = setTimeout(() => {
      setAssignSuccessToast("");
    }, 5000);
    return () => clearTimeout(timer);
  }, [assignSuccessToast]);

  // Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGrade, setSelectedGrade] = useState("all");
  const [selectedChapter, setSelectedChapter] = useState("all");
  const [selectedDifficulty, setSelectedDifficulty] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  // Expanded Accordion State
  const [expandedQuestionIds, setExpandedQuestionIds] = useState(() => new Set());
  const [expandedSetIds, setExpandedSetIds] = useState(() => new Set());

  const toggleQuestionExpand = (qId) => {
    setExpandedQuestionIds((prev) => {
      const next = new Set(prev);
      if (next.has(qId)) next.delete(qId);
      else next.add(qId);
      return next;
    });
  };

  const toggleSetExpand = (setId) => {
    setExpandedSetIds((prev) => {
      const next = new Set(prev);
      if (next.has(setId)) next.delete(setId);
      else next.add(setId);
      return next;
    });
  };

  // Load questions and sets concurrently
  const loadQuestions = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const [resQuestions, resSets, resMe] = await Promise.all([
        fetch(`${apiBase}/api/questions/my`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiBase}/api/questions/sets`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiBase}/api/me`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (!resQuestions.ok) {
        const body = await resQuestions.json().catch(() => ({}));
        throw new Error(body.error || "Failed to load questions");
      }
      const dataQ = await resQuestions.json();
      setQuestions(Array.isArray(dataQ.questions) ? dataQ.questions : []);

      if (resSets.ok) {
        const dataS = await resSets.json();
        setQuestionSets(Array.isArray(dataS.sets) ? dataS.sets : []);
      }

      if (resMe && resMe.ok) {
        const me = await resMe.json().catch(() => null);
        if (me?.role === "tutor") {
          setIsTutor(true);
          setActiveTab((prev) => (prev === "homework" ? "questions" : prev));
          if (searchParams.get("tab") === "homework") {
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.delete("tab");
              return next;
            }, { replace: true });
          }
          const resStudents = await fetch(`${apiBase}/api/tutor/students`, { headers: { Authorization: `Bearer ${token}` } });
          if (resStudents.ok) {
            const dataStudents = await resStudents.json().catch(() => ({}));
            setRosterStudents(dataStudents.students || []);
          }
        }
      }
    } catch (err) {
      setError(err.message || "Failed to load questions");
    } finally {
      setLoading(false);
    }
  }, [apiBase, token, searchParams, setSearchParams]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  // Filtered individual questions (only standalone questions, never questions belonging to a group/set)
  const filteredQuestions = questions.filter((q) => {
    if (q.setId || q.setName) return false;
    if (selectedGrade !== "all" && q.grade !== selectedGrade) return false;
    if (selectedChapter !== "all") {
      const qChapters = Array.isArray(q.chapters) && q.chapters.length
        ? q.chapters
        : (q.tags && q.tags.length ? q.tags : (q.chapter ? [q.chapter] : []));
      if (!qChapters.includes(selectedChapter)) return false;
    }
    if (selectedDifficulty !== "all" && q.difficulty !== selectedDifficulty) return false;
    if (selectedStatus !== "all" && q.latestStatus !== selectedStatus) return false;
    if (searchQuery.trim()) {
      const qLower = searchQuery.toLowerCase();
      const matchTitle = (q.title || "").toLowerCase().includes(qLower);
      const matchLatex = (q.latex || "").toLowerCase().includes(qLower);
      const matchGrade = (q.grade || "").toLowerCase().includes(qLower);
      const qChapters = Array.isArray(q.chapters) ? q.chapters : (q.tags || []);
      const matchChapter = qChapters.some((ch) => String(ch).toLowerCase().includes(qLower)) ||
        (q.chapter || "").toLowerCase().includes(qLower);
      if (!matchTitle && !matchLatex && !matchChapter && !matchGrade) return false;
    }
    return true;
  });

  // Filtered question groups
  const filteredQuestionSets = questionSets.filter((s) => {
    if (selectedGrade !== "all" && s.grade !== selectedGrade) return false;
    if (selectedChapter !== "all") {
      const sChapters = Array.isArray(s.chapters) && s.chapters.length
        ? s.chapters
        : (s.chapter ? [s.chapter] : []);
      if (!sChapters.includes(selectedChapter)) return false;
    }
    if (selectedStatus !== "all") {
      if (selectedStatus === "completed" && s.completedCount < s.questionCount) return false;
      if (selectedStatus === "in_progress" && s.inProgressCount === 0 && s.completedCount === 0) return false;
      if (selectedStatus === "not_attempted" && (s.completedCount > 0 || s.inProgressCount > 0)) return false;
    }
    if (searchQuery.trim()) {
      const qLower = searchQuery.toLowerCase();
      const matchTitle = (s.title || "").toLowerCase().includes(qLower);
      const matchDesc = (s.description || "").toLowerCase().includes(qLower);
      const matchGrade = (s.grade || "").toLowerCase().includes(qLower);
      const matchChapters = (s.chapters || []).some((ch) => String(ch).toLowerCase().includes(qLower));
      const matchChild = (s.questions || []).some(
        (q) => (q.title || "").toLowerCase().includes(qLower) || (q.latex || "").toLowerCase().includes(qLower)
      );
      if (!matchTitle && !matchDesc && !matchGrade && !matchChapters && !matchChild) return false;
    }
    return true;
  });

  // Bulk expand/collapse for individual questions
  const allQuestionsAreExpanded =
    filteredQuestions.length > 0 &&
    filteredQuestions.every((q) => expandedQuestionIds.has(q.id || q._id));

  const handleExpandAllQuestions = () => {
    setExpandedQuestionIds(new Set(filteredQuestions.map((q) => q.id || q._id)));
  };

  const handleCollapseAllQuestions = () => {
    setExpandedQuestionIds(new Set());
  };

  // Bulk expand/collapse for question groups
  const allSetsAreExpanded =
    filteredQuestionSets.length > 0 &&
    filteredQuestionSets.every((s) => expandedSetIds.has(s.id));

  const handleExpandAllSets = () => {
    setExpandedSetIds(new Set(filteredQuestionSets.map((s) => s.id)));
  };

  const handleCollapseAllSets = () => {
    setExpandedSetIds(new Set());
  };

  const getDifficultyClass = (difficulty) => {
    const normalized = String(difficulty || "").toUpperCase();
    if (normalized === "HOT") return "diff-hot";
    if (normalized === "LOT") return "diff-lot";
    return "diff-mot";
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "graded":
        return <span className="attempt-badge" style={{ backgroundColor: "#059669", color: "#ffffff", border: "1px solid #047857", fontWeight: "700" }}>✓ Graded</span>;
      case "submitted":
        return <span className="attempt-badge" style={{ backgroundColor: "#4338ca", color: "#ffffff", border: "1px solid #3730a3", fontWeight: "700" }}>🔒 Submitted</span>;
      case "completed":
        return <span className="attempt-badge attempt-badge-accepted" style={{ fontWeight: "700" }}>Completed</span>;
      case "needs_review":
        return <span className="attempt-badge attempt-badge-review" style={{ fontWeight: "700" }}>Needs Review</span>;
      case "in_progress":
        return <span className="attempt-badge" style={{ backgroundColor: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd", fontWeight: "700" }}>In Progress</span>;
      default:
        return <span className="attempt-badge" style={{ backgroundColor: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", fontWeight: "600" }}>Not Started</span>;
    }
  };

  // Chapter options for filter dropdown based on selected grade
  const filterChapterOptions = selectedGrade !== "all" && CURRICULUM[selectedGrade]
    ? CURRICULUM[selectedGrade]
    : ALL_CHAPTERS;

  return (
    <section className="dashboard-page" style={{ width: "100%", maxWidth: "1200px", margin: "0 auto", padding: "24px 20px", boxSizing: "border-box" }}>
      {/* ── Top Header Banner ── */}
      <div className="admin-frame-editor-header" style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "20px",
        flexWrap: "wrap",
        gap: "16px",
        backgroundColor: "#ffffff",
        padding: "20px 24px",
        borderRadius: "14px",
        border: "1px solid var(--border)",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
      }}>
        <div>
          <h1 style={{ margin: "0 0 4px 0", fontSize: "24px", fontWeight: "700", color: "#0f172a" }}>My Questions</h1>
          <div className="question-meta" style={{ fontSize: "14px", color: "var(--muted)" }}>
            Practice questions from your homework, exams, or handwritten notes on your interactive slate.
          </div>
        </div>
        <button
          type="button"
          className="primary-button"
          onClick={() => navigate("/questions/create")}
          style={{ padding: "10px 20px", fontSize: "14px", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px" }}
        >
          <span>✍️</span> + Upload / Write Problem
        </button>
      </div>

      {error && <div className="error-text" style={{ marginBottom: "16px" }}>{error}</div>}

      {/* ── Assignment Dispatch Success Toast ── */}
      {assignSuccessToast && (
        <div style={{
          backgroundColor: "#d1fae5",
          color: "#065f46",
          border: "1px solid #6ee7b7",
          borderRadius: "10px",
          padding: "12px 18px",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}>
          <span>🎉 {assignSuccessToast}</span>
          <button
            type="button"
            onClick={() => setAssignSuccessToast("")}
            style={{ background: "none", border: "none", color: "#065f46", cursor: "pointer", fontWeight: "700" }}
          >
            ✕
          </button>
        </div>
      )}

      {/* ── Tutor Classroom Notice Banner ── */}
      {isTutor && (
        <div style={{
          backgroundColor: "#f5f3ff",
          border: "1px solid #ddd6fe",
          borderRadius: "12px",
          padding: "12px 18px",
          marginBottom: "18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "10px"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "20px" }}>🧑‍🏫</span>
            <div>
              <strong style={{ fontSize: "14px", color: "#4c1d95" }}>Tutor Classroom Mode Active</strong>
              <div style={{ fontSize: "12px", color: "#6d28d9" }}>
                Select questions or question sets below and click <strong>📋 Assign</strong> to dispatch homework to your enrolled students.
              </div>
            </div>
          </div>
          <button
            type="button"
            className="secondary-button"
            onClick={() => navigate("/tutor/assignments")}
            style={{ fontSize: "12px", padding: "6px 14px", backgroundColor: "#ffffff", color: "#6d28d9", borderColor: "#c4b5fd" }}
          >
            View Tutor Assignments Dashboard →
          </button>
        </div>
      )}

      {/* ── Tab Switcher: All Questions vs Question Groups ── */}
      <div className="questions-tab-bar">
        <button
          type="button"
          className={`questions-tab-pill ${activeTab === "questions" ? "active" : ""}`}
          onClick={() => handleTabChange("questions")}
        >
          <span>All Questions</span>
          <span className="questions-tab-badge">{filteredQuestions.length}</span>
        </button>

        <button
          type="button"
          className={`questions-tab-pill ${activeTab === "groups" ? "active" : ""}`}
          onClick={() => handleTabChange("groups")}
        >
          <span>Question Groups</span>
          <span className="questions-tab-badge">{filteredQuestionSets.length}</span>
        </button>
      </div>

      {/* ── Filter Bar ── */}
      <div style={{
        display: "flex",
        gap: "12px",
        flexWrap: "wrap",
        alignItems: "center",
        marginBottom: "20px",
        backgroundColor: "#ffffff",
        padding: "14px 18px",
        borderRadius: "12px",
        border: "1px solid var(--border)"
      }}>
        <div style={{ flex: "1 1 200px" }}>
          <input
            type="text"
            placeholder={activeTab === "groups" ? "Search groups by title or topic..." : "Search problems by title, topic, or chapter..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1px solid var(--border)" }}
          />
        </div>

        {/* Grade Filter */}
        <select
          value={selectedGrade}
          onChange={(e) => {
            setSelectedGrade(e.target.value);
            setSelectedChapter("all");
          }}
          style={FILTER_DROPDOWN_STYLE}
        >
          <option value="all">All Grades</option>
          {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>

        {/* Chapter Filter */}
        <select
          value={selectedChapter}
          onChange={(e) => setSelectedChapter(e.target.value)}
          style={FILTER_DROPDOWN_STYLE}
        >
          <option value="all">All Chapters</option>
          {filterChapterOptions.map((ch) => <option key={ch} value={ch}>{ch}</option>)}
        </select>

        {/* Difficulty Filter (only for individual questions tab) */}
        {activeTab === "questions" && (
          <select
            value={selectedDifficulty}
            onChange={(e) => setSelectedDifficulty(e.target.value)}
            style={FILTER_DROPDOWN_STYLE}
          >
            <option value="all">All Difficulties</option>
            <option value="LOT">LOT (Foundational)</option>
            <option value="MOT">MOT (Standard)</option>
            <option value="HOT">HOT (Challenging)</option>
          </select>
        )}

        {/* Status Filter */}
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          style={FILTER_DROPDOWN_STYLE}
        >
          <option value="all">All Statuses</option>
          <option value="completed">Completed</option>
          {activeTab === "questions" && <option value="needs_review">Needs Review</option>}
          <option value="in_progress">In Progress</option>
          <option value="not_attempted">Not Started</option>
        </select>

        <span className="question-meta" style={{ marginLeft: "auto", fontSize: "13px" }}>
          {activeTab === "groups"
            ? `${filteredQuestionSets.length} ${filteredQuestionSets.length === 1 ? "group" : "groups"}`
            : `${filteredQuestions.length} ${filteredQuestions.length === 1 ? "question" : "questions"}`}
        </span>
      </div>

      {/* ── TAB 1: ALL QUESTIONS ── */}
      {activeTab === "questions" && (
        <div>
          {loading ? (
            <div className="analysis-box" style={{ textAlign: "center", padding: "40px" }}>
              <div className="question-meta">Loading your questions...</div>
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div className="analysis-box" style={{ textAlign: "center", padding: "60px 20px" }}>
              <div style={{ fontSize: "42px", marginBottom: "12px" }}>📝</div>
              <h3 style={{ margin: "0 0 8px 0" }}>No questions found</h3>
              <p className="question-meta" style={{ maxWidth: "420px", margin: "0 auto 20px auto" }}>
                {questions.length === 0
                  ? "You haven't added any practice questions yet. Take a photo, upload an image, or write a problem to practice on your slate!"
                  : "No questions match your selected filter criteria."}
              </p>
              {questions.length === 0 && (
                <button className="primary-button" type="button" onClick={() => navigate("/questions/create")}>
                  Upload Your First Question
                </button>
              )}
            </div>
          ) : (
            <div className="question-block" style={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", overflow: "hidden" }}>
              <div className="question-title-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "10px" }}>
                <div className="question-meta">Questions ({filteredQuestions.length})</div>
                {filteredQuestions.length > 0 && (
                  <div className="frame-list-controls" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {isTutor && (
                      <button
                        type="button"
                        className="frame-bulk-toggle-btn"
                        onClick={() => {
                          setIsSelectMode((prev) => {
                            if (prev) setSelectedQuestionIds(new Set());
                            return !prev;
                          });
                        }}
                        style={isSelectMode ? { backgroundColor: "#4f46e5", color: "#ffffff", borderColor: "#4f46e5" } : {}}
                      >
                        {isSelectMode ? "Done Selecting" : "☑️ Select Questions"}
                      </button>
                    )}
                    <button
                      type="button"
                      className="frame-bulk-toggle-btn"
                      onClick={allQuestionsAreExpanded ? handleCollapseAllQuestions : handleExpandAllQuestions}
                    >
                      {allQuestionsAreExpanded ? "Collapse All" : "Expand All"}
                    </button>
                  </div>
                )}
              </div>

              {/* Tutor Common Select Action Bar */}
              {isTutor && isSelectMode && (
                <div
                  style={{
                    backgroundColor: "#f8fafc",
                    border: "1.5px solid #6366f1",
                    borderRadius: "12px",
                    padding: "12px 18px",
                    marginBottom: "16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "12px",
                    boxShadow: "0 2px 4px rgba(99, 102, 241, 0.08)"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                    <span style={{ fontWeight: "700", color: "#1e1b4b", fontSize: "14px" }}>
                      {selectedQuestionIds.size} of {filteredQuestions.length} selected
                    </span>
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => {
                        if (selectedQuestionIds.size === filteredQuestions.length) {
                          setSelectedQuestionIds(new Set());
                        } else {
                          setSelectedQuestionIds(new Set(filteredQuestions.map((q) => q.id || q._id)));
                        }
                      }}
                      style={{ fontSize: "12px", padding: "4px 10px" }}
                    >
                      {selectedQuestionIds.size === filteredQuestions.length ? "Deselect All" : "Select All"}
                    </button>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="secondary-button"
                      disabled={selectedQuestionIds.size === 0}
                      onClick={() => {
                        setExpandedQuestionIds((prev) => {
                          const next = new Set(prev);
                          selectedQuestionIds.forEach((id) => next.add(id));
                          return next;
                        });
                      }}
                      style={{ fontSize: "12px", padding: "6px 14px", opacity: selectedQuestionIds.size === 0 ? 0.5 : 1 }}
                      title="Expand solution drawers for all selected questions"
                    >
                      📖 Expand Selected ({selectedQuestionIds.size})
                    </button>

                    <button
                      type="button"
                      className="primary-button"
                      disabled={selectedQuestionIds.size === 0}
                      onClick={() => {
                        if (selectedQuestionIds.size === 1) {
                          const singleId = Array.from(selectedQuestionIds)[0];
                          const singleQ = questions.find((q) => (q.id || q._id) === singleId);
                          if (singleQ) openAssignModalForSingle(singleQ);
                        } else {
                          openAssignModalForBatch();
                        }
                      }}
                      style={{
                        fontSize: "12px",
                        padding: "6px 16px",
                        opacity: selectedQuestionIds.size === 0 ? 0.5 : 1,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px"
                      }}
                    >
                      <span>📋</span>
                      <span>Assign Selected to Classroom ({selectedQuestionIds.size})</span>
                    </button>

                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => {
                        setIsSelectMode(false);
                        setSelectedQuestionIds(new Set());
                      }}
                      style={{ fontSize: "12px", padding: "6px 12px" }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div className="frame-list">
                {filteredQuestions.map((q, index) => {
                  const questionId = q.id || q._id;
                  const isExpanded = expandedQuestionIds.has(questionId);
                  const difficultyClass = getDifficultyClass(q.difficulty);
                  const dateStr = q.createdAt
                    ? new Date(q.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
                    : "";
                  const questionChapters = Array.isArray(q.chapters) && q.chapters.length
                    ? q.chapters
                    : (Array.isArray(q.tags) && q.tags.length ? q.tags : (q.chapter ? [q.chapter] : []));
                  const attemptCount = Number(q.attemptCount || 0);

                  return (
                    <div
                      key={questionId}
                      className={`frame-horizontal-card ${isExpanded ? "is-expanded" : ""}`}
                    >
                      {/* Collapsed Mode / Card Bar */}
                      <div
                        className="frame-card-bar"
                        onClick={() => toggleQuestionExpand(questionId)}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleQuestionExpand(questionId);
                          }
                        }}
                      >
                        <div className="frame-bar-left">
                          {isTutor && isSelectMode && (
                            <input
                              type="checkbox"
                              checked={selectedQuestionIds.has(questionId)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleSelectQuestion(questionId);
                              }}
                              style={{
                                cursor: "pointer",
                                width: "16px",
                                height: "16px",
                                marginRight: "8px",
                                accentColor: "#4f46e5"
                              }}
                              title="Select question"
                            />
                          )}
                          <span className="frame-circle-num">{index + 1}</span>
                          <div className="frame-preview-content">
                            {q.latex ? (
                              <div className="frame-preview-math">
                                <BlockMath
                                  math={formatLatexForDisplay(q.latex)}
                                  renderError={() => (
                                    <span className="question-fallback-text">{q.latex}</span>
                                  )}
                                />
                              </div>
                            ) : (
                              <span className="question-empty-text">{q.title || "Untitled Question"}</span>
                            )}
                          </div>
                        </div>

                        <div className="frame-bar-right" onClick={(e) => e.stopPropagation()}>
                          <div className="frame-badges-cluster">
                            {q.setName && (
                              <span style={{
                                backgroundColor: "#ede9fe",
                                color: "#6d28d9",
                                padding: "2px 8px",
                                borderRadius: "999px",
                                fontSize: "11px",
                                fontWeight: "700",
                                whiteSpace: "nowrap"
                              }}>
                                📚 {q.setName} #{q.setOrder || 1}
                              </span>
                            )}
                            <span className={`frame-diff-pill ${difficultyClass}`}>
                              {q.difficulty || "MOT"}
                            </span>
                            <span className="frame-attempts-pill">
                              {attemptCount} {attemptCount === 1 ? "attempt" : "attempts"}
                            </span>
                            {getStatusBadge(q.latestStatus)}
                          </div>

                          <div className="frame-bar-actions">

                            <button
                              type="button"
                              className="frame-quick-practice-btn"
                              onClick={() => navigate(`/practice/custom/${questionId}`, { state: { backTo: `/my-questions?tab=${activeTab}` } })}
                              title={attemptCount > 0 ? "Solve Again" : "Solve Now"}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                                <polygon points="5 3 19 12 5 21 5 3"></polygon>
                              </svg>
                              <span>{attemptCount > 0 ? "Solve Again" : "Solve Now"}</span>
                            </button>

                            <button
                              type="button"
                              className="frame-chevron-btn"
                              onClick={() => toggleQuestionExpand(questionId)}
                              aria-label={isExpanded ? "Collapse question details" : "Expand question details"}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className={`chevron-icon ${isExpanded ? "rotated" : ""}`}
                              >
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Mode Drawer */}
                      {isExpanded && (
                        <div className="frame-expanded-drawer">
                          <div className="frame-drawer-top">
                            <div className="frame-drawer-title-row">
                              <h4 className="frame-drawer-title">{q.title || "Custom Problem"}</h4>
                              {q.setName && (
                                <span style={{
                                  backgroundColor: "#ede9fe",
                                  color: "#6d28d9",
                                  padding: "2px 8px",
                                  borderRadius: "999px",
                                  fontSize: "11px",
                                  fontWeight: "700"
                                }}>
                                  Assignment: {q.setName} (#{q.setOrder || 1})
                                </span>
                              )}
                              {dateStr && (
                                <span className="frame-drawer-date">
                                  Added on: {dateStr}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="frame-drawer-question-section">
                            <div className="frame-drawer-section-label">Problem Statement</div>
                            <div className="frame-drawer-math-wrap">
                              {q.latex ? (
                                <BlockMath
                                  math={formatLatexForDisplay(q.latex)}
                                  renderError={() => (
                                    <span className="question-fallback-text">{q.latex}</span>
                                  )}
                                />
                              ) : (
                                <span className="question-empty-text">No equation text</span>
                              )}
                            </div>

                            {q.imageUrl && (
                              <div className="frame-drawer-figure" style={{ flexDirection: "column", alignItems: "flex-start", gap: "8px" }}>
                                <div className="frame-drawer-section-label" style={{ marginBottom: 0 }}>Reference Image</div>
                                <img
                                  src={q.imageUrl}
                                  alt="Question reference"
                                  style={{
                                    maxHeight: "200px",
                                    maxWidth: "100%",
                                    borderRadius: "8px",
                                    border: "1px solid #cbd5e1",
                                    objectFit: "contain",
                                    backgroundColor: "#fff"
                                  }}
                                />
                              </div>
                            )}
                          </div>

                          <div className="frame-drawer-tags-section" style={{ display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "center" }}>
                            <div className="frame-tags-group">
                              <span className="tags-label">Grade:</span>
                              <span className="frame-tag-pill strength-pill">{q.grade || "Grade 10"}</span>
                            </div>
                            {questionChapters.length > 0 && (
                              <div className="frame-tags-group">
                                <span className="tags-label">Chapters:</span>
                                {questionChapters.map((ch, i) => (
                                  <span key={i} className="frame-tag-pill focus-pill">{ch}</span>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="frame-drawer-actions" style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "4px" }}>
                            {isTutor && (
                              <button
                                type="button"
                                className="secondary-button"
                                onClick={() => openAssignModalForSingle(q)}
                                style={{
                                  padding: "8px 16px",
                                  fontSize: "13px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  backgroundColor: "#f5f3ff",
                                  color: "#6d28d9",
                                  borderColor: "#ddd6fe",
                                  fontWeight: "600"
                                }}
                              >
                                <span>📋</span>
                                <span>Assign to Classroom</span>
                              </button>
                            )}
                            {attemptCount > 0 && (
                              <Link
                                to={`/my-questions/${questionId}/attempts`}
                                className="secondary-button"
                                style={{ padding: "8px 16px", fontSize: "13px", display: "inline-flex", alignItems: "center", gap: "6px" }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10" />
                                  <polyline points="12 6 12 12 16 14" />
                                </svg>
                                <span>{`View Attempts (${attemptCount})`}</span>
                              </Link>
                            )}
                            <button
                              type="button"
                              className="frame-cta-practice-slate"
                              style={{ padding: "8px 18px", fontSize: "13px" }}
                              onClick={() => navigate(`/practice/custom/${questionId}`, { state: { backTo: `/my-questions?tab=${activeTab}` } })}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <polygon points="5 3 19 12 5 21 5 3"></polygon>
                              </svg>
                              <span>{attemptCount > 0 ? "Solve Again" : "Solve Now"}</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TAB 2: QUESTION GROUPS (ASSIGNMENTS / WORKSHEETS) ── */}
      {activeTab === "groups" && (
        <div>
          {loading ? (
            <div className="analysis-box" style={{ textAlign: "center", padding: "40px" }}>
              <div className="question-meta">Loading question groups...</div>
            </div>
          ) : filteredQuestionSets.length === 0 ? (
            <div className="analysis-box" style={{ textAlign: "center", padding: "60px 20px" }}>
              <div style={{ fontSize: "42px", marginBottom: "12px" }}>📚</div>
              <h3 style={{ margin: "0 0 8px 0" }}>No question groups found</h3>
              <p className="question-meta" style={{ maxWidth: "440px", margin: "0 auto 20px auto" }}>
                {questionSets.length === 0
                  ? "Upload homework sheets, worksheets, or exam papers to create question sets that you can solve sequentially on your slate!"
                  : "No question groups match your current filter criteria."}
              </p>
              <button
                className="primary-button"
                type="button"
                onClick={() => navigate("/questions/create")}
                style={{ padding: "10px 22px", fontWeight: "700" }}
              >
                + Upload Homework / Assignment
              </button>
            </div>
          ) : (
            <div>
              {/* Question Groups Header Row */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", flexWrap: "wrap", gap: "10px" }}>
                <div className="question-meta">
                  Question Groups ({filteredQuestionSets.length})
                </div>
                <div className="frame-list-controls" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {isTutor && (
                    <button
                      type="button"
                      className="frame-bulk-toggle-btn"
                      onClick={() => {
                        setIsGroupSelectMode((prev) => {
                          if (prev) setSelectedGroupIds(new Set());
                          return !prev;
                        });
                      }}
                      style={isGroupSelectMode ? { backgroundColor: "#4f46e5", color: "#ffffff", borderColor: "#4f46e5" } : {}}
                    >
                      {isGroupSelectMode ? "Done Selecting" : "☑️ Select Groups"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="frame-bulk-toggle-btn"
                    onClick={allSetsAreExpanded ? handleCollapseAllSets : handleExpandAllSets}
                  >
                    {allSetsAreExpanded ? "Collapse All Groups" : "Expand All Groups"}
                  </button>
                </div>
              </div>

              {/* Tutor Common Select Action Bar for Question Groups */}
              {isTutor && isGroupSelectMode && (
                <div
                  style={{
                    backgroundColor: "#f8fafc",
                    border: "1.5px solid #6366f1",
                    borderRadius: "12px",
                    padding: "12px 18px",
                    marginBottom: "16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "12px",
                    boxShadow: "0 2px 4px rgba(99, 102, 241, 0.08)"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                    <span style={{ fontWeight: "700", color: "#1e1b4b", fontSize: "14px" }}>
                      {selectedGroupIds.size} of {filteredQuestionSets.length} selected
                    </span>
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => {
                        if (selectedGroupIds.size === filteredQuestionSets.length) {
                          setSelectedGroupIds(new Set());
                        } else {
                          setSelectedGroupIds(new Set(filteredQuestionSets.map((s) => s.id)));
                        }
                      }}
                      style={{ fontSize: "12px", padding: "4px 10px" }}
                    >
                      {selectedGroupIds.size === filteredQuestionSets.length ? "Deselect All" : "Select All"}
                    </button>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="secondary-button"
                      disabled={selectedGroupIds.size === 0}
                      onClick={() => {
                        setExpandedSetIds((prev) => {
                          const next = new Set(prev);
                          selectedGroupIds.forEach((id) => next.add(id));
                          return next;
                        });
                      }}
                      style={{ fontSize: "12px", padding: "6px 14px", opacity: selectedGroupIds.size === 0 ? 0.5 : 1 }}
                      title="Expand all selected question groups"
                    >
                      📖 Expand Selected Groups ({selectedGroupIds.size})
                    </button>

                    <button
                      type="button"
                      className="primary-button"
                      disabled={selectedGroupIds.size === 0}
                      onClick={() => {
                        const firstGroupId = Array.from(selectedGroupIds)[0];
                        const targetGroup = questionSets.find((s) => s.id === firstGroupId);
                        if (targetGroup) openAssignModalForSet(targetGroup);
                      }}
                      style={{
                        fontSize: "12px",
                        padding: "6px 16px",
                        opacity: selectedGroupIds.size === 0 ? 0.5 : 1,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px"
                      }}
                    >
                      <span>📋</span>
                      <span>Assign Selected Group to Classroom</span>
                    </button>

                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => {
                        setIsGroupSelectMode(false);
                        setSelectedGroupIds(new Set());
                      }}
                      style={{ fontSize: "12px", padding: "6px 12px" }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* List of Question Group Accordion Cards */}
              <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                {filteredQuestionSets.map((s) => {
                  const isExpanded = expandedSetIds.has(s.id);
                  const isSetTest = s.assignmentType === "test" || s.sourceType === "exam_paper";
                  const dateStr = s.createdAt
                    ? new Date(s.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
                    : "";
                  const percentSolved = s.questionCount > 0
                    ? Math.round((s.completedCount / s.questionCount) * 100)
                    : 0;

                  return (
                    <div
                      key={s.id}
                      className={`group-row-card ${isExpanded ? "is-expanded" : ""}`}
                    >
                      {/* Set Row Header (Folded Trigger) */}
                      <div
                        className="group-row-header"
                        onClick={() => toggleSetExpand(s.id)}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleSetExpand(s.id);
                          }
                        }}
                      >
                        {/* Left: Icon & Name */}
                        <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: "1 1 260px", minWidth: 0 }}>
                          {isTutor && isGroupSelectMode && (
                            <input
                              type="checkbox"
                              checked={selectedGroupIds.has(s.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleSelectGroup(s.id);
                              }}
                              style={{
                                cursor: "pointer",
                                width: "16px",
                                height: "16px",
                                accentColor: "#4f46e5"
                              }}
                              title="Select group"
                            />
                          )}
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
                              <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: "#0f172a" }}>
                                {s.title}
                              </h3>
                              <span
                                style={{
                                  backgroundColor: s.assignmentType === "test" || s.sourceType === "exam_paper" ? "#fee2e2" : "#eff6ff",
                                  color: s.assignmentType === "test" || s.sourceType === "exam_paper" ? "#dc2626" : "#2563eb",
                                  border: `1px solid ${s.assignmentType === "test" || s.sourceType === "exam_paper" ? "#fca5a5" : "#bfdbfe"}`,
                                  padding: "2px 8px",
                                  borderRadius: "6px",
                                  fontSize: "11px",
                                  fontWeight: "700",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px"
                                }}
                              >
                                <span>{s.assignmentType === "test" || s.sourceType === "exam_paper" ? "⏱️" : "📝"}</span>
                                <span>{s.assignmentType === "test" || s.sourceType === "exam_paper" ? "Timed Exam" : "Homework Set"}</span>
                              </span>
                              {s.targetGoal && s.targetGoal !== "general" && (
                                <span
                                  style={{
                                    backgroundColor: "#ede9fe",
                                    color: "#6d28d9",
                                    border: "1px solid #ddd6fe",
                                    padding: "2px 8px",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "600"
                                  }}
                                >
                                  🎯 {GOAL_LABELS[s.targetGoal] || s.targetGoal}
                                </span>
                              )}
                              {isSetTest && s.isGraded && s.totalMarksAwarded !== null && s.totalMarksAwarded !== undefined ? (
                                <span
                                  style={{
                                    backgroundColor: "#ecfdf5",
                                    color: "#065f46",
                                    border: "1px solid #a7f3d0",
                                    padding: "2px 8px",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "700"
                                  }}
                                >
                                  🏆 Score: {s.totalMarksAwarded} / {s.totalMarks || s.questionCount * 10} Marks ({s.totalMarks > 0 ? Math.round((s.totalMarksAwarded / s.totalMarks) * 100) : 0}%)
                                </span>
                              ) : isSetTest && s.totalMarks > 0 ? (
                                <span
                                  style={{
                                    backgroundColor: "#fef3c7",
                                    color: "#92400e",
                                    border: "1px solid #fde68a",
                                    padding: "2px 8px",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "700"
                                  }}
                                >
                                  💯 {s.totalMarks} Marks
                                </span>
                              ) : null}
                              <span style={{
                                backgroundColor: "#eff6ff",
                                color: "#1d4ed8",
                                padding: "2px 8px",
                                borderRadius: "999px",
                                fontSize: "11px",
                                fontWeight: "700"
                              }}>
                                {s.questionCount} {s.questionCount === 1 ? "Problem" : "Problems"}
                              </span>
                            </div>
                            <div style={{ fontSize: "12px", color: "var(--muted)" }}>
                              {dateStr && <span>Added {dateStr} • </span>}
                              <span>{s.sourceType || "Homework / Assignment"}</span>
                            </div>
                          </div>
                        </div>

                        {/* Middle: Badges & Solved Progress */}
                        <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }} onClick={(e) => e.stopPropagation()}>
                          <span className="frame-tag-pill strength-pill">
                            {s.grade || "Grade 11"}
                          </span>

                          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                            {(s.chapters || []).slice(0, 2).map((ch, ci) => (
                              <span key={ci} className="frame-tag-pill focus-pill">
                                {ch}
                              </span>
                            ))}
                            {(s.chapters || []).length > 2 && (
                              <span style={{ fontSize: "11px", color: "var(--muted)", alignSelf: "center" }}>
                                +{(s.chapters || []).length - 2}
                              </span>
                            )}
                          </div>

                          {/* Progress Tracker */}
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <div className="group-progress-track" title={`${percentSolved}% completed`}>
                              <div className="group-progress-fill" style={{ width: `${percentSolved}%` }} />
                            </div>
                            <span style={{ fontSize: "12px", fontWeight: "700", color: s.completedCount === s.questionCount ? "#059669" : "#475569", whiteSpace: "nowrap" }}>
                              {s.completedCount} / {s.questionCount} Solved
                            </span>
                          </div>
                        </div>

                        {/* Right: Actions (Fixed to right, same CTA label as single questions, icon-only chevron) */}
                        <div className="frame-bar-actions" style={{ marginLeft: "auto", flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>

                          {s.firstQuestionId && (
                            <button
                              type="button"
                              className="frame-quick-practice-btn"
                              onClick={() => {
                                if (s.isLocked || s.isGraded) {
                                  toggleSetExpand(s.id);
                                } else {
                                  navigate(`/practice/custom/${s.firstQuestionId}?setId=${s.id}`, { state: { backTo: `/my-questions?tab=${activeTab}` } });
                                }
                              }}
                              title={s.isGraded ? "✓ Graded (Click to view questions & marks)" : (s.isLocked ? "Exam Submitted (Click to view questions)" : (s.completedCount > 0 ? "Solve Again" : "Solve Now"))}
                              style={
                                s.isGraded
                                  ? { backgroundColor: "#059669", color: "#ffffff", border: "1px solid #047857", fontWeight: "700", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }
                                  : s.isLocked
                                  ? { backgroundColor: "#4338ca", color: "#ffffff", border: "1px solid #3730a3", fontWeight: "700", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }
                                  : {}
                              }
                            >
                              {s.isGraded ? (
                                <span>✓ Graded</span>
                              ) : s.isLocked ? (
                                <span>🔒 Submitted</span>
                              ) : (
                                <>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                  </svg>
                                  <span>{s.completedCount > 0 ? "Solve Again" : "Solve Now"}</span>
                                </>
                              )}
                            </button>
                          )}

                          <button
                            type="button"
                            className="frame-chevron-btn"
                            onClick={() => toggleSetExpand(s.id)}
                            aria-label={isExpanded ? "Collapse question set" : "Expand question set"}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className={`chevron-icon ${isExpanded ? "rotated" : ""}`}
                            >
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Unfolded Mode: Questions in this Set (Matching Single Question UX) */}
                      {isExpanded && (
                        <div className="group-drawer-list">
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", flexWrap: "wrap", gap: "8px" }}>
                            <div style={{ fontSize: "13px", fontWeight: "700", color: "#334155" }}>
                              Problems in this Assignment ({s.questions?.length || 0}):
                            </div>
                            {isTutor && (
                              <button
                                type="button"
                                className="secondary-button"
                                onClick={() => openAssignModalForSet(s)}
                                style={{
                                  padding: "5px 12px",
                                  fontSize: "12px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  backgroundColor: "#f5f3ff",
                                  color: "#6d28d9",
                                  borderColor: "#ddd6fe",
                                  fontWeight: "600"
                                }}
                              >
                                <span>📋</span>
                                <span>{(s.assignedTo && s.assignedTo.length > 0) || s.assignedAll ? "Update Assignment / Reassign" : "Assign Set to Classroom"}</span>
                              </button>
                            )}
                          </div>

                          {(!s.questions || s.questions.length === 0) ? (
                            <div className="question-meta" style={{ padding: "12px", textAlign: "center" }}>
                              No questions loaded for this set.
                            </div>
                          ) : (
                            s.questions.map((q, qIndex) => {
                              const childQId = q.id || q._id;
                              const isChildExpanded = expandedQuestionIds.has(childQId);
                              const difficultyClass = getDifficultyClass(q.difficulty);
                              const qAttemptCount = Number(q.attemptCount || 0);
                              const childChapters = Array.isArray(q.chapters) && q.chapters.length
                                ? q.chapters
                                : (q.chapter ? [q.chapter] : (s.chapters || []));
                              const isSetTest = s.assignmentType === "test" || s.sourceType === "exam_paper";

                              return (
                                <div
                                  key={childQId}
                                  className={`frame-horizontal-card ${isChildExpanded ? "is-expanded" : ""}`}
                                >
                                  {/* Collapsed Bar for Child Question */}
                                  <div
                                    className="frame-card-bar"
                                    onClick={() => toggleQuestionExpand(childQId)}
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={isChildExpanded}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        toggleQuestionExpand(childQId);
                                      }
                                    }}
                                  >
                                    <div className="frame-bar-left">
                                      <span className="frame-circle-num">{q.setOrder || (qIndex + 1)}</span>
                                      <div className="frame-preview-content">
                                        {q.latex ? (
                                          <div className="frame-preview-math">
                                            <BlockMath
                                              math={formatLatexForDisplay(q.latex)}
                                              renderError={() => (
                                                <span className="question-fallback-text">{q.latex}</span>
                                              )}
                                            />
                                          </div>
                                        ) : (
                                          <span className="question-empty-text">{q.title || `Problem ${qIndex + 1}`}</span>
                                        )}
                                      </div>
                                    </div>

                                    <div className="frame-bar-right" onClick={(e) => e.stopPropagation()}>
                                      <div className="frame-badges-cluster">
                                        <span className={`frame-diff-pill ${difficultyClass}`}>
                                          {q.difficulty || "MOT"}
                                        </span>
                                        {qAttemptCount > 0 && (
                                          <span className="frame-attempts-pill">
                                            {qAttemptCount} {qAttemptCount === 1 ? "attempt" : "attempts"}
                                          </span>
                                        )}
                                        {isSetTest && q.gradesPublished && Number.isFinite(Number(q.marksAwarded)) && (
                                          <span
                                            style={{
                                              backgroundColor: "#d1fae5",
                                              color: "#065f46",
                                              border: "1px solid #a7f3d0",
                                              borderRadius: "6px",
                                              padding: "2px 8px",
                                              fontSize: "11px",
                                              fontWeight: "700",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: "4px"
                                            }}
                                          >
                                            💯 {q.marksAwarded} / {q.marks || 10} Marks
                                          </span>
                                        )}
                                        {getStatusBadge(isSetTest && q.gradesPublished ? "graded" : (q.isSubmitted ? "submitted" : (q.status || q.latestStatus)))}
                                      </div>

                                      <div className="frame-bar-actions">
                                        {q.latestAttemptId && (
                                          <button
                                            type="button"
                                            className="frame-quick-practice-btn"
                                            onClick={() => navigate(`/attempts/${q.latestAttemptId}`, { state: { backTo: `/my-questions?tab=${activeTab}` } })}
                                            style={{
                                              fontSize: "11px",
                                              padding: "5px 10px",
                                              whiteSpace: "nowrap",
                                              backgroundColor: "#1e40af",
                                              color: "#ffffff",
                                              border: "1px solid #1d4ed8",
                                              fontWeight: "600",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: "4px"
                                            }}
                                            title="Review your work and teacher evaluation"
                                          >
                                            <span>👁️ Review Attempt</span>
                                          </button>
                                        )}

                                        {!q.isLocked && !s.isLocked && !(isSetTest && (q.isSubmitted || q.status === "submitted" || q.status === "completed")) && (
                                          <button
                                            type="button"
                                            className="frame-quick-practice-btn"
                                            onClick={() => navigate(`/practice/custom/${childQId}?setId=${s.id}`, { state: { backTo: `/my-questions?tab=${activeTab}` } })}
                                            title={q.status === "completed" ? "Solve Again" : "Solve Now"}
                                            style={{ fontSize: "11px", padding: "5px 10px" }}
                                          >
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                              <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                            </svg>
                                            <span>{q.status === "completed" ? "Solve Again" : "Solve Now"}</span>
                                          </button>
                                        )}

                                        {!q.latestAttemptId && (q.isLocked || (isSetTest && s.isLocked)) && (
                                          <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 600, padding: "4px 8px" }}>
                                            🔒 Locked
                                          </span>
                                        )}

                                        <button
                                          type="button"
                                          className="frame-chevron-btn"
                                          onClick={() => toggleQuestionExpand(childQId)}
                                          aria-label={isChildExpanded ? "Collapse question details" : "Expand question details"}
                                        >
                                          <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className={`chevron-icon ${isChildExpanded ? "rotated" : ""}`}
                                          >
                                            <polyline points="6 9 12 15 18 9"></polyline>
                                          </svg>
                                        </button>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Expanded Drawer: Full Question View */}
                                  {isChildExpanded && (
                                    <div className="frame-expanded-drawer">
                                      <div className="frame-drawer-header">
                                        <div className="frame-drawer-title-row">
                                          <h4 className="frame-drawer-question-title">
                                            {q.title || `Problem ${qIndex + 1}`}
                                          </h4>
                                          <span style={{
                                            backgroundColor: "#ede9fe",
                                            color: "#6d28d9",
                                            padding: "2px 8px",
                                            borderRadius: "999px",
                                            fontSize: "11px",
                                            fontWeight: "700"
                                          }}>
                                            Assignment: {s.title} (#{q.setOrder || (qIndex + 1)})
                                          </span>
                                        </div>
                                      </div>

                                      <div className="frame-drawer-question-section">
                                        <div className="frame-drawer-section-label">Problem Statement</div>
                                        <div className="frame-drawer-math-wrap">
                                          {q.latex ? (
                                            <BlockMath
                                              math={formatLatexForDisplay(q.latex)}
                                              renderError={() => (
                                                <span className="question-fallback-text">{q.latex}</span>
                                              )}
                                            />
                                          ) : (
                                            <span className="question-empty-text">No equation text</span>
                                          )}
                                        </div>

                                        {q.imageUrl && (
                                          <div className="frame-drawer-figure" style={{ flexDirection: "column", alignItems: "flex-start", gap: "8px" }}>
                                            <div className="frame-drawer-section-label" style={{ marginBottom: 0 }}>Reference Image</div>
                                            <img
                                              src={q.imageUrl}
                                              alt="Question reference"
                                              style={{
                                                maxHeight: "200px",
                                                maxWidth: "100%",
                                                borderRadius: "8px",
                                                border: "1px solid #cbd5e1",
                                                objectFit: "contain",
                                                backgroundColor: "#fff"
                                              }}
                                            />
                                          </div>
                                        )}
                                      </div>

                                      <div className="frame-drawer-tags-section" style={{ display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "center" }}>
                                        <div className="frame-tags-group">
                                          <span className="tags-label">Grade:</span>
                                          <span className="frame-tag-pill strength-pill">{q.grade || s.grade || "Grade 10"}</span>
                                        </div>
                                        {childChapters.length > 0 && (
                                          <div className="frame-tags-group">
                                            <span className="tags-label">Chapters:</span>
                                            {childChapters.map((ch, i) => (
                                              <span key={i} className="frame-tag-pill focus-pill">{ch}</span>
                                            ))}
                                          </div>
                                        )}
                                      </div>

                                      <div className="frame-drawer-actions" style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "4px" }}>
                                        {qAttemptCount > 0 && (
                                          <Link
                                            to={`/my-questions/${childQId}/attempts`}
                                            className="secondary-button"
                                            style={{ padding: "8px 16px", fontSize: "13px", display: "inline-flex", alignItems: "center", gap: "6px" }}
                                          >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                              <circle cx="12" cy="12" r="10" />
                                              <polyline points="12 6 12 12 16 14" />
                                            </svg>
                                            <span>{`View Attempts (${qAttemptCount})`}</span>
                                          </Link>
                                        )}
                                        <button
                                          type="button"
                                          className="frame-cta-practice-slate"
                                          style={{ padding: "8px 18px", fontSize: "13px" }}
                                          onClick={() => navigate(`/practice/custom/${childQId}?setId=${s.id}`, { state: { backTo: `/my-questions?tab=${activeTab}` } })}
                                        >
                                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                            <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                          </svg>
                                          <span>{qAttemptCount > 0 ? "Solve Again" : "Solve Now"}</span>
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TAB 3: REDIRECT TO MY TUTOR ── */}
      {!isTutor && activeTab === "homework" && (
        <div className="analysis-box" style={{ textAlign: "center", padding: "60px 20px" }}>
          <h3 style={{ margin: "0 0 8px 0" }}>Homework has moved to My Tutor</h3>
          <p className="question-meta" style={{ maxWidth: "460px", margin: "0 auto 20px auto" }}>
            All your tutor-assigned homework, tests, and scheduled live sessions are now organized in your dedicated My Tutor hub.
          </p>
          <button
            className="primary-button"
            type="button"
            onClick={() => navigate("/my-tutor")}
            style={{ padding: "10px 24px", marginTop: "12px" }}
          >
            Go to My Tutor
          </button>
        </div>
      )}

      {/* ── Floating Bulk Assignment Action Bar ── */}
      {isTutor && selectedQuestionIds.size > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: "24px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 999,
            backgroundColor: "#0f172a",
            color: "#ffffff",
            padding: "12px 24px",
            borderRadius: "14px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.35)",
            display: "flex",
            alignItems: "center",
            gap: "18px",
            border: "1px solid rgba(255, 255, 255, 0.15)"
          }}
        >
          <span style={{ fontSize: "14px", fontWeight: "600" }}>
            ✓ {selectedQuestionIds.size} {selectedQuestionIds.size === 1 ? "question" : "questions"} selected
          </span>
          <button
            type="button"
            className="primary-button"
            onClick={openAssignModalForBatch}
            style={{ backgroundColor: "#4f46e5", padding: "8px 18px", fontSize: "13px", fontWeight: "700" }}
          >
            📋 Assign Selected as Homework
          </button>
          <button
            type="button"
            onClick={() => setSelectedQuestionIds(new Set())}
            style={{
              background: "transparent",
              border: "none",
              color: "#94a3b8",
              cursor: "pointer",
              fontSize: "13px",
              textDecoration: "underline"
            }}
          >
            Clear Selection
          </button>
        </div>
      )}

      {/* ── Tutor Assign Homework Modal ── */}
      <AssignHomeworkModal
        isOpen={Boolean(assignModalData)}
        onClose={() => setAssignModalData(null)}
        data={assignModalData}
        rosterStudents={rosterStudents}
        apiBase={apiBase}
        token={token}
        onSuccess={(msg) => {
          setAssignSuccessToast(msg);
          setSelectedQuestionIds(new Set());
          loadQuestions();
        }}
      />
    </section>
  );
}
